import { supabase } from "@/integrations/supabase/client";
import { saveOrgProfilePatch, type OrgProfilePatch } from "@/lib/orgProfilePatch";

/**
 * Website scan for the onboarding wizard's "scan my website" toggle.
 *
 * The same `import-org-profile` scraper the profile editor uses, but instead of
 * staging per-field suggestions for one-by-one review, this auto-applies what it
 * finds straight into the DRAFT profile. Everything it writes stays a draft the
 * admin edits in the following wizard steps and the editor — nothing publishes.
 *
 * It never clobbers a field the admin already filled: `skip` names the profile
 * keys to leave alone (the values they typed on the brand step), so the scan
 * fills blanks rather than overwriting deliberate input.
 *
 * IT ALSO NEVER TOUCHES A FIELD A STAFF MEMBER HAS STAGED, and the enforcement
 * of that rule is a SINGLE SERVER-SIDE TRANSACTION — not this file.
 * `accept_uncontested_owner_suggestions` locks the candidate rows, re-checks per
 * field whether any other pending row for that (org, field) was created by
 * staff, and returns the ids it actually accepted alongside the ones it refused.
 * It never updates a staff-created row.
 *
 * ORDER MATTERS, and it is: DECIDE, then SAVE, then (on a save failure) REVERT.
 *
 * - Deciding first is what makes the guarantee real, because both weaker orders
 *   lose it. Saving first writes a value the server may then refuse to accept —
 *   exactly the defect this rewrite closes: the patch must be built ONLY from
 *   rows the server actually cleared. And a client-side re-read followed by a
 *   separate accept call leaves a window: a staff row committing between the two
 *   would be superseded by the accept, the wizard silently winning a conflict
 *   the owner never saw.
 * - The client-side staff filter below is therefore a CHEAP PRE-FILTER ONLY, and
 *   so is the wizard's preflight count. Neither is a correctness boundary; both
 *   exist to avoid work whose result would be thrown away, and to explain the
 *   omission to the admin.
 * - A save that fails AFTER the accept would otherwise consume suggestions
 *   without their values reaching the profile, so the accepted rows are reverted
 *   to `pending` best-effort and the scan reports failure. Leaving them pending
 *   is recoverable in the editor; consuming them is not.
 */

/**
 * Scraper field keys that map to a wizard-collected or editor profile field and
 * are safe to auto-apply. The scraper can surface others; those are ignored here
 * and remain reviewable in the editor.
 */
const APPLICABLE_KEYS = [
  "tagline",
  "description",
  "missionStatement",
  "whatMakesUsDifferent",
  "ourStory",
  "serviceTags",
  "selectedBenefits",
  "marketsServed",
  "foundedYear",
] as const;

/** Why a scan returned `ok: false`. Reported so the wizard can pick its message. */
export type ScanFailureReason =
  | "import_failed"
  | "read_failed"
  /**
   * The deciding RPC does not exist in this project yet (bundle ahead of the
   * migration). We cannot decide safely, so nothing is applied.
   */
  | "rpc_missing"
  | "decide_failed"
  | "save_failed";

export interface ScanResult {
  ok: boolean;
  /** How many suggestions the scraper produced. */
  found: number;
  /** The patch actually written to the draft, so the caller can reseed local state. */
  applied: OrgProfilePatch;
  /**
   * Field keys the scan deliberately left to the owner because a staff-staged
   * card exists for them — the client pre-filter's fields plus every field the
   * server refused with a staff reason. The wizard tells the admin to review
   * those in the profile editor rather than silently filling nothing.
   */
  skippedForStaff: string[];
  /**
   * Field keys the server declined for a non-staff reason (`already_decided`,
   * `not_found`): the row was consumed or vanished between the read and the
   * decision. Nothing is owed to the admin here, so it is reported separately
   * and never triggers the "staff prepared details" message.
   */
  skippedOther: string[];
  /** Set only when `ok` is false. */
  reason?: ScanFailureReason;
}

interface PendingRow {
  id: string;
  field_key: string;
  suggested_value: unknown;
  /** NULL when the owner's own import staged the row; a uuid when staff did. */
  created_by_staff_user_id: string | null;
  extracted_at?: string | null;
  created_at?: string | null;
}

/** One entry of the deciding RPC's response. */
interface DecisionEntry {
  id: string;
  field_key: string;
  reason?: string;
}

interface Decision {
  accepted: DecisionEntry[];
  skipped: DecisionEntry[];
}

const empty = (reason: ScanFailureReason): ScanResult => ({
  ok: false,
  found: 0,
  applied: {},
  skippedForStaff: [],
  skippedOther: [],
  reason,
});

const isStaffRow = (row: { created_by_staff_user_id?: string | null }) =>
  row.created_by_staff_user_id !== null && row.created_by_staff_user_id !== undefined;

/** The server's two staff refusals. Any other reason is not the admin's concern. */
const STAFF_REASONS = new Set(["staff_pending", "staff_row"]);

/**
 * Newest-first ordering for two OWNER rows on the same field. Pending
 * uniqueness has been (org, field, value) since 20260903120000, so two differing
 * owner values for one field are possible even though a single import run
 * should not produce them. Choosing by row order would be choosing by whatever
 * Postgres happened to return, so choose explicitly: latest `extracted_at`, then
 * latest `created_at`, then the larger id as a last-resort stable tiebreak.
 */
const rowRank = (row: PendingRow): [number, number, string] => [
  Date.parse(row.extracted_at ?? "") || 0,
  Date.parse(row.created_at ?? "") || 0,
  row.id ?? "",
];

const isNewer = (a: PendingRow, b: PendingRow): boolean => {
  const [ax, ay, az] = rowRank(a);
  const [bx, by, bz] = rowRank(b);
  if (ax !== bx) return ax > bx;
  if (ay !== by) return ay > by;
  return az > bz;
};

/** The pending rows for one org, or null when they could not be read. */
const readPendingRows = async (orgId: string): Promise<PendingRow[] | null> => {
  const { data, error } = await supabase
    .from("org_profile_suggestions")
    // types regen after 20260903120000 lands
    .select("id, field_key, suggested_value, created_by_staff_user_id, extracted_at, created_at")
    .eq("org_id", orgId)
    .eq("status", "pending");

  if (!error) return (data ?? []) as unknown as PendingRow[];

  // Deploy race: a bundle carrying the staff column can reach a browser before
  // migration 20260903120000 adds it, and Postgres answers 42703
  // (undefined_column). No column means no staff row can exist yet, so read the
  // pre-Console shape and treat every row as the owner's rather than failing the
  // scan outright. Any other error is a real read failure and fails the scan.
  if ((error as { code?: string }).code !== "42703") return null;

  const { data: legacy, error: legacyError } = await supabase
    .from("org_profile_suggestions")
    .select("id, field_key, suggested_value, extracted_at, created_at")
    .eq("org_id", orgId)
    .eq("status", "pending");
  if (legacyError) return null;
  return ((legacy ?? []) as unknown as PendingRow[]).map((row) => ({
    ...row,
    created_by_staff_user_id: null,
  }));
};

/** A PostgREST/Postgres "that function does not exist" answer. */
const isMissingFunction = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;
  if (code === "PGRST202" || code === "42883") return true;
  const message = String((error as { message?: string } | null)?.message ?? "");
  return /could not find the function|function .* does not exist/i.test(message);
};

/**
 * Decide, atomically and server-side, which of the owner's candidate rows may be
 * applied. One transaction locks the candidates and, per field, refuses any
 * whose field also carries a pending staff row; the rest are accepted and the
 * owner's other pending rows for those fields superseded. Staff rows are never
 * written.
 *
 * A failure is reported rather than worked around: an undecided row is a row
 * that may be staff-contested, so the caller applies NOTHING.
 */
const decideOwnerCandidates = async (
  ids: string[],
): Promise<{ decision: Decision } | { failure: ScanFailureReason }> => {
  const { data, error } = await supabase.rpc(
    // types regen after 20260904190000 lands
    "accept_uncontested_owner_suggestions" as never,
    { p_suggestion_ids: ids } as never,
  );

  if (error) {
    // The bundle can be ahead of migration 20260904190000, exactly as it can be
    // ahead of 20260903120000 above. Unlike a missing COLUMN, a missing FUNCTION
    // is not evidence that no staff row exists — the staging table may be fully
    // populated — so there is no safe degraded path. Do NOT fall back to the
    // direct per-row update: that is the unguarded write this replaces.
    return { failure: isMissingFunction(error) ? "rpc_missing" : "decide_failed" };
  }

  const decision = data as unknown as Decision | null;
  if (!decision || !Array.isArray(decision.accepted) || !Array.isArray(decision.skipped)) {
    // A shape we do not recognise is not permission to apply anything.
    return { failure: "decide_failed" };
  }
  return { decision };
};

export const scanWebsiteIntoDraft = async (
  orgId: string,
  skip: Set<string> = new Set(),
): Promise<ScanResult> => {
  // 1. Run the scraper. It reads the org's website column (saved before this is
  //    called) and writes pending suggestions server-side.
  const { error: importError } = await supabase.functions.invoke("import-org-profile", {
    body: { orgId },
  });
  if (importError) return empty("import_failed");

  // 2. Read the pending suggestions, INCLUDING who staged each one.
  const suggestions = await readPendingRows(orgId);
  if (suggestions === null) return empty("read_failed");
  const found = suggestions.length;

  // 3. Cheap client-side pre-filter, UX only: a field that ALREADY shows a
  //    staff-staged card is not worth sending to the server, and naming it for
  //    the admin costs nothing. The server re-decides every candidate anyway, so
  //    nothing here is load-bearing.
  const staffFields = new Set(suggestions.filter(isStaffRow).map((s) => s.field_key));
  const skippedForStaff = new Set(staffFields);
  const skippedOther = new Set<string>();

  // 4. Choose the OWNER's candidate rows: applicable, not already filled by
  //    hand, one deterministically chosen row per field.
  const chosen = new Map<string, PendingRow>();
  for (const s of suggestions) {
    if (isStaffRow(s)) continue;
    if (staffFields.has(s.field_key)) continue;
    if (!(APPLICABLE_KEYS as readonly string[]).includes(s.field_key)) continue;
    if (skip.has(s.field_key)) continue;
    const prev = chosen.get(s.field_key);
    if (!prev || isNewer(s, prev)) chosen.set(s.field_key, s);
  }

  const candidates = [...chosen.values()];
  if (candidates.length === 0) {
    return {
      ok: true,
      found,
      applied: {},
      skippedForStaff: [...skippedForStaff].sort(),
      skippedOther: [],
    };
  }

  // 5. DECIDE FIRST, on the server, in one transaction — before anything is
  //    written to the profile.
  const outcome = await decideOwnerCandidates(candidates.map((row) => row.id));
  if ("failure" in outcome) {
    return {
      ok: false,
      found,
      applied: {},
      skippedForStaff: [...skippedForStaff].sort(),
      skippedOther: [],
      reason: outcome.failure,
    };
  }

  for (const entry of outcome.decision.skipped) {
    if (!entry?.field_key) continue;
    if (STAFF_REASONS.has(String(entry.reason))) skippedForStaff.add(entry.field_key);
    else skippedOther.add(entry.field_key);
  }

  // 6. Build the patch ONLY from the rows the server cleared. A candidate the
  //    server refused never reaches the profile.
  const byId = new Map(candidates.map((row) => [row.id, row]));
  const acceptedIds: string[] = [];
  const applied: Record<string, unknown> = {};
  for (const entry of outcome.decision.accepted) {
    const row = byId.get(entry?.id);
    if (!row) continue;
    acceptedIds.push(row.id);
    // foundedYear is stored/typed as a string everywhere the profile reads it.
    applied[row.field_key] =
      row.field_key === "foundedYear" ? String(row.suggested_value) : row.suggested_value;
  }

  if (acceptedIds.length === 0) {
    return {
      ok: true,
      found,
      applied: {},
      skippedForStaff: [...skippedForStaff].sort(),
      skippedOther: [...skippedOther].sort(),
    };
  }

  // 7. Apply to the draft through the canonical writer.
  const saved = await saveOrgProfilePatch(orgId, applied as OrgProfilePatch);
  if (saved.status !== "saved") {
    // The suggestions are decided but their values never landed. Put them back
    // so the editor re-offers them, rather than leaving the admin with a profile
    // missing values the system has recorded as applied. The org admin's column
    // grant on this table is exactly (status, decided_by, decided_at), which is
    // what this writes and no more. Best-effort: if the revert also fails there
    // is nothing further to try from the client.
    const { error: revertError } = await supabase
      .from("org_profile_suggestions")
      .update({ status: "pending", decided_by: null, decided_at: null } as never)
      .in("id", acceptedIds);
    if (revertError) {
      console.error("orgWebsiteScan: could not revert accepted suggestions", revertError);
    }
    return {
      ok: false,
      found,
      applied: {},
      skippedForStaff: [...skippedForStaff].sort(),
      skippedOther: [...skippedOther].sort(),
      reason: "save_failed",
    };
  }

  return {
    ok: true,
    found,
    applied: applied as OrgProfilePatch,
    skippedForStaff: [...skippedForStaff].sort(),
    skippedOther: [...skippedOther].sort(),
  };
};
